import { MONTHS_SHORT, dayParts, weekdayOf } from '@/lib/market/calendar'

export interface AxisTick {
    index: number
    label: string
}

const magnitudeOf = (value: number): number => {
    let magnitude = 1
    if (!(value > 0) || !Number.isFinite(value)) return 1

    while (magnitude * 10 <= value) magnitude *= 10
    while (magnitude > value) magnitude /= 10

    return magnitude
}

export const niceStep = (span: number, count: number): number => {
    const raw = span / Math.max(1, count)
    if (!(raw > 0) || !Number.isFinite(raw)) return 1

    const magnitude = magnitudeOf(raw)
    const normalized = raw / magnitude
    const factor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10

    return factor * magnitude
}

export const decimalsFor = (step: number): number => {
    if (step >= 1) return 0

    let decimals = 0
    let value = step

    while (decimals < 6 && Math.abs(value - Math.round(value)) > 1e-9) {
        value *= 10
        decimals++
    }

    return decimals
}

export const niceTicks = (min: number, max: number, count = 5): number[] => {
    if (!Number.isFinite(min) || !Number.isFinite(max)) return []
    if (min === max) return [min]

    const step = niceStep(max - min, count)
    const decimals = decimalsFor(step)
    const start = Math.ceil(min / step - 1e-9)
    const end = Math.floor(max / step + 1e-9)
    const ticks: number[] = []

    for (let index = start; index <= end && ticks.length < 50; index++) {
        ticks.push(Number((index * step).toFixed(decimals)))
    }

    return ticks
}

export const niceDomain = (min: number, max: number, count = 5, pad = 0.06, includeZero = false): { min: number, max: number, ticks: number[] } => {
    let low = min
    let high = max

    if (!Number.isFinite(low) || !Number.isFinite(high)) {
        low = 0
        high = 1
    }

    if (includeZero) {
        low = Math.min(low, 0)
        high = Math.max(high, 0)
    }

    if (low === high) {
        const spread = Math.abs(low) * 0.02 || 1
        low -= spread
        high += spread
    }

    const padding = (high - low) * pad
    const paddedLow = includeZero && low === 0 ? 0 : low - padding
    const paddedHigh = includeZero && high === 0 ? 0 : high + padding
    const ticks = niceTicks(paddedLow, paddedHigh, count)

    return { min: paddedLow, max: paddedHigh, ticks }
}

export const scaleLinear = (domain: [number, number], range: [number, number]) => {
    const [d0, d1] = domain
    const [r0, r1] = range
    const span = d1 - d0 || 1

    return (value: number): number => r0 + ((value - d0) * (r1 - r0)) / span
}

export const dayTicks = (days: number[], width: number): AxisTick[] => {
    const count = days.length
    if (!count) return []

    const first = days[0]
    const last = days[count - 1]
    const spanDays = last - first
    const candidates: AxisTick[] = []
    let previousMonth = -1
    let previousYear = -1

    days.forEach((day, index) => {
        const { year, month, date } = dayParts(day)

        if (spanDays <= 45) {
            const weekday = weekdayOf(day)
            const previousWeekday = index > 0 ? weekdayOf(days[index - 1]) : -1
            if (index === 0 || weekday < previousWeekday) candidates.push({ index, label: `${date} ${MONTHS_SHORT[month]}` })

            return
        }

        if (spanDays <= 420) {
            if (month !== previousMonth) {
                candidates.push({ index, label: month === 0 || index === 0 ? `${MONTHS_SHORT[month]} ${String(year).slice(2)}` : MONTHS_SHORT[month] })
            }
        }
        else if (spanDays <= 1200) {
            if (month !== previousMonth && month % 3 === 0) {
                candidates.push({ index, label: `${MONTHS_SHORT[month]} ${String(year).slice(2)}` })
            }
        }
        else if (year !== previousYear) {
            candidates.push({ index, label: String(year) })
        }

        previousMonth = month
        previousYear = year
    })

    const filtered = candidates.filter(tick => tick.index > 0 || candidates.length === 1)
    const step = width / Math.max(1, count)
    const minimumGap = 64
    const result: AxisTick[] = []
    let lastX = -Infinity

    for (const tick of filtered.length ? filtered : candidates) {
        const x = tick.index * step

        if (x - lastX >= minimumGap) {
            result.push(tick)
            lastX = x
        }
    }

    return result
}
